import { StreamController } from "webrtc-player/webrtc_controller/stream_controller";
import {
  useState,
  useRef,
  useEffect,
  useContext,
  memo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { SignalContext } from "context/SignalContext";
import clsx from "clsx";
import { SvgRefresh } from "ui/svgs";
import { PlusIcon, MinusIcon } from "@heroicons/react/24/outline";
import React from "react";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import {
  STREAM_TYPE_LIVE,
  STREAM_TYPE_PLAYBACK,
} from "webrtc-player/common/base";
import { WebRTC } from "webrtc-player/webrtc/webrtc";
import PtzController from "./ptz-controller";
import { getTokenByActionAndResourceId, errorHandler } from "lib/utils";
import { ResDeviceType, PtzAreaZoomParams } from "webrtc-player/common/base";
import { StreamWebRTC } from "webrtc-player/webrtc/stream_webrtc";
import { useDebouncedCallback } from "use-debounce";
import { PubSub } from "pubsub-js";
import moment from "moment";
import { AuthContext } from "context/AuthContext";
import { DB_STORE, ENV } from "lib/constants";
import { throttle } from "lodash";
import { ControllerBase } from "webrtc-player/webrtc_controller/webrtc_controller";
import { WebRTCBase } from "webrtc-player/webrtc/base";
import { showToast } from "ui/components/common";
import Cookies from "js-cookie";

const CANVAS_ID_PREFIX = "canvas_streaming_";

const CameraPlayer = forwardRef(function CameraPlayer(props: any, ref: any) {
  const {
    streamingCamera,
    streamType,
    playBackProps,
    visible,
    onStreamClosed,
    onStreamOpen,
    onStreamRetry,
    selected,
    onSelect,
    children,
    muted,
    showPtz,
    ptzMode,
    streamId,
    onFullScreen,
    contentFillColor,
  } = props;
  const {
    startAt,
    endAt,
    pause,
    onPauseChange,
    absoluteSeekTime,
    onProgressChange,
  } = playBackProps || {};
  console.log("ptzMode", ptzMode);
  const refContainer = useRef(null);
  const visibleInactiveRef = useRef(null);
  const refVideo = useRef<HTMLVideoElement>(null);
  const refAudio = useRef(null);
  const initRef = useRef(false);
  const refCanvasWrapper = useRef<HTMLDivElement>(null);
  const refReactZoomPanPinchRef = useRef<ReactZoomPanPinchRef>(null);
  const streamControllerRef = useRef<StreamController>();
  const connIdRef = useRef("");
  const autoRetryTimesRef = useRef(0);
  const openTimeStampRef = useRef(0);
  const { signal, wsIsOpen, getDb } = useContext(SignalContext);
  const [firstFrameDrew, setFirstFrameDrew] = useState(false);
  const [indexDBBlob, setIndexDBBlob] = useState<Blob>();
  const [streamState, setStreamState] = useState("free");
  const [connectionText, setConnectionText] = useState("");
  const { user } = useContext(AuthContext);
  const [seekPoint, setSeekPoint] = useState(0);
  const visibleRef = useRef(null);
  const startAtRef = useRef(startAt);
  const currentOffsetRef = useRef(0);

  useEffect(() => {
    startAtRef.current = startAt;
  }, [startAt]);

  const inactiveHandler = () => {
    console.log("inactiveHandler");
    setStreamState("inActive");
    if (streamType === STREAM_TYPE_LIVE) {
      closeStreaming();
    }
    if (streamType === STREAM_TYPE_PLAYBACK) {
      onPauseChange?.(true);
    }
  };
  const activeHandler = () => {
    console.log("activeHandler visible", visibleRef.current);
    //active only when the page is visible
    if (visibleRef.current === false) return false;
    if (streamType === STREAM_TYPE_LIVE) {
      playVideo();
    }
    if (streamType === STREAM_TYPE_PLAYBACK) {
      setStreamState("open");
      onPauseChange?.(false);
    }
  };
  // when player turns invisible disconnected in 10s and resume as visible
  useEffect(() => {
    visibleRef.current = visible;
    // do not trigger at first render to avoid duplicate play
    if (!initRef.current) {
      return;
    }
    if (visible) {
      clearTimeout(visibleInactiveRef.current);
      visibleInactiveRef.current = null;
      if (
        !streamControllerRef.current ||
        !streamControllerRef.current.isWebRTCConnected()
      ) {
        activeHandler();
      }
    } else if (!visibleInactiveRef.current) {
      visibleInactiveRef.current = setTimeout(inactiveHandler, 1000 * 10);
    }
  }, [visible]);

  useEffect(() => {
    const subscriptionInactive = PubSub.subscribe(
      "onPageInactive",
      inactiveHandler,
    );
    const subscriptionActive = PubSub.subscribe("onPageActive", activeHandler);
    const subscriptionUserInactive = PubSub.subscribe("onUserInactive", () => {
      if (streamingCamera.enablePlayLiveStreamTimeoutOut) {
        inactiveHandler();
      }
    });
    const subscriptionUserActive = PubSub.subscribe("onUserActive", () => {
      if (streamingCamera.enablePlayLiveStreamTimeoutOut) {
        activeHandler();
      }
    });
    return () => {
      PubSub.unsubscribe("onPageInactive", subscriptionInactive);
      PubSub.unsubscribe("onPageActive", subscriptionActive);
      PubSub.unsubscribe("onUserInactive", subscriptionUserInactive);
      PubSub.unsubscribe("onUserActive", subscriptionUserActive);
      // mark the page is removed
      refContainer.current = null;
    };
  }, []);
  // useEffect(() => {
  //   if (streamType === STREAM_TYPE_PLAYBACK) {
  //     return;
  //   }
  //   if (!signal || !wsIsOpen) {
  //     return;
  //   }
  //   autoRetryTimesRef.current = 0;
  //   playVideo();
  // }, [signal, wsIsOpen, streamId, streamingCamera]);

  useEffect(() => {
    if (!visible) return;
    if (streamType === STREAM_TYPE_PLAYBACK && startAt < 0) {
      return;
    }
    if (!signal || !wsIsOpen) {
      return;
    }
    console.log("Start playVideo, first record date", startAt);
    autoRetryTimesRef.current = 0;
    playVideo();
  }, [signal, wsIsOpen, startAt, streamingCamera, streamId, visible]);

  useEffect(() => {
    if (!refAudio.current) {
      return;
    }
    refAudio.current.muted = muted;
  }, [muted, refAudio]);

  useEffect(() => {
    if (streamType !== STREAM_TYPE_PLAYBACK) {
      return;
    }
    if (absoluteSeekTime > 0 && startAt >= 0) {
      const seconds = Math.max(
        Math.floor((absoluteSeekTime - startAt) / 1000),
        0,
      );
      setSeekPoint(seconds);
    }
  }, [absoluteSeekTime, startAt]);

  useEffect(() => {
    console.log("new seek point useffect---", seekPoint);
    if (
      streamState === "open" &&
      seekPoint >= 0 &&
      streamType === STREAM_TYPE_PLAYBACK
    ) {
      console.log("goseek---", seekPoint);
      setTimeout(() => {
        streamControllerRef.current.seek(Math.round(seekPoint));
        setSeekPoint(-1);
      }, 0);
      onPauseChange?.(false);
    }
  }, [seekPoint, streamState]);

  useEffect(() => {
    if (
      signal &&
      wsIsOpen &&
      streamControllerRef.current &&
      streamType === STREAM_TYPE_PLAYBACK
    ) {
      if (pause) {
        streamControllerRef.current.pause();
      } else {
        streamControllerRef.current.resume();
      }
    }
  }, [pause]);

  //handling resize
  useEffect(() => {
    if (!refContainer.current) return;
    const resizeObserver = new ResizeObserver(() => {
      refReactZoomPanPinchRef.current?.resetTransform();
    });
    resizeObserver.observe(refContainer.current);
    return () => resizeObserver.disconnect(); // clean up
  }, []);

  useEffect(() => {
    return () => {
      console.log("call close streamControllerRef", streamControllerRef);
      closeStreaming();
    };
  }, [streamControllerRef]);
  //get placeHolder image from indexedDB
  useEffect(() => {
    getDb().then((db) => {
      db.read(DB_STORE.SNAPSHOT, +streamingCamera.cameraId).then((data) => {
        setIndexDBBlob((data as { imageBlob: Blob })?.imageBlob);
      });
    });
    return () => {
      setIndexDBBlob(null);
    };
  }, [streamingCamera]);

  useEffect(() => {
    if (!refAudio.current) {
      return;
    }
    refAudio.current.muted = true;
  }, [refAudio]);

  function playVideo() {
    console.log("start play video");
    if (visible.current === false || !streamingCamera) return;
    openTimeStampRef.current = Date.now();
    streamType === STREAM_TYPE_PLAYBACK ? playPlayBackVideo() : playLiveVideo();
  }

  function playPlayBackVideo() {
    console.log("playPlayBackVideo");
    closeStreaming();
    //no need to block by firstframe when playback
    setFirstFrameDrew(true);
    const cameraMac = streamingCamera.cameraMac;
    const remoteIndex = streamingCamera.remoteIndex;

    const video = refVideo.current;
    const audio = refAudio.current;
    const canvas = getCanvas();
    const streamParams = {
      cameraMac,
      remoteIndex,
      streamId: streamId,
      startAt: startAt / 1000,
      endAt: Math.floor(endAt / 1000),
    };
    console.log("playback streamParams", streamParams);
    const streamControllerParams = {
      streamParams: streamParams,
      streamType: STREAM_TYPE_PLAYBACK,
      video,
      audio,
      canvas,
    };
    createStreamController(streamControllerParams);
  }

  function playLiveVideo() {
    closeStreaming();
    setFirstFrameDrew(false);

    const cameraMac = streamingCamera.cameraMac;
    const remoteIndex = streamingCamera.remoteIndex;

    const video = refVideo.current;
    const audio = refAudio.current;
    const canvas = getCanvas();
    let _streamId;
    if (streamingCamera?.capabilities?.streamNum > 1) {
      _streamId = streamId ?? 1;
    } else {
      _streamId = 0;
    }
    const streamParams = { cameraMac, remoteIndex, streamId: _streamId };
    const streamControllerParams = {
      streamParams: streamParams,
      streamType: STREAM_TYPE_LIVE,
      video,
      audio,
      canvas,
    };
    console.log("www param", streamParams);
    createStreamController(streamControllerParams);
  }
  function createStreamController(params) {
    const turn = streamingCamera.turn;
    const rtcToken = streamingCamera.rtcToken;
    let cookieOrgId = Cookies.get("orgId");
    if (cookieOrgId === "undefined") cookieOrgId = null;
    const orgId: number = streamingCamera.orgId
      ? +streamingCamera.orgId
      : +cookieOrgId;
    const nvrId: number = +streamingCamera.nvrId;

    const createdstreamController = new StreamController(
      {
        orgId,
        nvrId,
        signal,
        turn,
        rtcToken,
        debugging: Boolean(ENV === "development"),
        errorHandler,
        //@ts-ignore
        videoCodec: window.WebRTCVideoCodec,
      },
      params,
    );
    createdstreamController.webRTC.addEventListener(
      WebRTCBase.ACTION_FIRST_FRAME,
      () => {
        setFirstFrameDrew(true);
      },
    );
    createdstreamController.webRTC.addEventListener(
      WebRTC.EVENT_MESSAGE_OPEN,
      () => {
        console.info(
          "message channel is reveived: connId=%s",
          createdstreamController.connectId,
          "connId",
          connIdRef.current,
        );
        // this param controls if visible listener.(off screen will not play)
        initRef.current = true;
        if (createdstreamController.connectId === connIdRef.current) {
          autoRetryTimesRef.current = 0;
          setConnectionText("");
          setStreamState("open");
          onStreamOpen?.();
        } else {
          createdstreamController.close();
        }
      },
    );
    createdstreamController.addEventListener(
      ControllerBase.EVENT_CLOSE,
      (reason) => {
        console.log("close triggered", reason);
        if (
          reason !== ControllerBase.CLOSE_NORMAL &&
          // @ts-ignore
          !window.LiveStreamAutoRetryDisabled
        ) {
          onStreamRetry?.(reason);
          let interval = 0;
          if (reason === ControllerBase.CLOSE_BUSY) {
            // very slow retry when nvr is busy
            interval = 1000 * 10;
          } else if (autoRetryTimesRef.current < 10) {
            // fast retry
            console.info("start to retry streaming: fast retry");
            interval = 5000;
          } else {
            // slow retry after 10 tries
            interval = 1000 * 10 - (Date.now() - openTimeStampRef.current);
            if (interval < 0) {
              interval = 0;
            }
          }

          currentOffsetRef.current > 0 &&
            setSeekPoint(currentOffsetRef.current);
          setTimeout(() => {
            if (createdstreamController.connectId === connIdRef.current) {
              playVideo();
              autoRetryTimesRef.current++;
            }
          }, interval);
        } else {
          onStreamClosed?.(reason);
          connIdRef.current = null;
        }
      },
    );
    createdstreamController.addEventListener(
      ControllerBase.EVENT_ERROR,
      (error) => {
        error?.external?.bandwidthConnFull &&
          setConnectionText("Bandwidth/Connection Full");

        if (createdstreamController.connectId === connIdRef.current) {
          console.info(
            "message channel is error: connId=%s",
            createdstreamController.connectId,
          );
          setStreamState("error");
        }
      },
    );
    createdstreamController.addEventListener(
      StreamController.EVENT_TIME_PROGRESS,
      (currentOffset) => {
        // const progressTime =
        //   currentTime + firstRecordTimestamp - startAtRef.current / 1000;
        currentOffsetRef.current = currentOffset;
        onProgressChange?.(currentOffset);
      },
    );
    connIdRef.current = createdstreamController.connectId;
    createdstreamController.start(); //
    streamControllerRef.current = createdstreamController;
    setStreamState("connecting");
    return createdstreamController;
  }
  function getCanvas() {
    if (!refCanvasWrapper.current) {
      return;
    }
    refCanvasWrapper.current.innerHTML = "";
    const canvas = document.createElement("canvas");
    canvas.id = CANVAS_ID_PREFIX + streamingCamera.cameraId;
    canvas.className =
      "canvas h-full w-full max-w-full object-contain aspect-video absolute z-[0] pointer-events-none";
    refCanvasWrapper.current.appendChild(canvas);
    return canvas;
  }

  function closeStreaming() {
    if (streamControllerRef.current) {
      connIdRef.current = null;
      streamControllerRef.current.close();
      streamControllerRef.current = null;
    }
  }

  function onPlaySpeedChange(speed: number, cb: (error, data) => void) {
    streamControllerRef.current?.speed(speed, (error, _data) => {
      if (_data.error) {
        switch (_data.error.code) {
          case 1039:
            showToast(1, "NVR connections reached limit");
            break;
          case 1038:
            showToast(1, "NVR bandwidth reached limit");
            break;
          case 1040:
            showToast(1, "NVR is testing bandwidth");
            break;
          default:
            showToast(1, "speed change failed");
        }
      }

      cb?.(error, _data);
    });
  }

  useImperativeHandle(ref, () => ({
    closeStreaming,
    takeSnapshots,
    onPlaySpeedChange,
    ptz,
  }));

  function handleSelectCamera() {
    onSelect?.(streamingCamera);
  }

  function handleDoubleClickCamera() {
    //hijack the double click event to fullscreen
    if (onFullScreen) {
      onFullScreen();
      return;
    }
    const elem = refContainer.current;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      // Firefox
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      // Chrome, Safari, and Opera
      elem.webkitRequestFullscreen();
    }
  }

  const takeSnapshots = () => {
    const canvas = document.getElementById(
      CANVAS_ID_PREFIX + streamingCamera?.cameraId.toString(),
    ) as HTMLCanvasElement;
    const dataURL = canvas.toDataURL("image/png");
    const link = document.createElement("a");

    link.href = dataURL;
    const formattedName = moment().format("MM-DD hh_mm_ss");
    link.download = `snapshot-${formattedName}.jpg`;

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
  };

  const handleUpdatePtzSnapshot = useDebouncedCallback(() => {
    PubSub.publish("updatePtzSnapshot", {
      siteId: +streamingCamera.siteId,
      cameraId: +streamingCamera.cameraId,
    });
  }, 5000);

  async function ptz(cmd: string) {
    //console.log("ptz start: ", cmd);
    if (streamControllerRef.current?.isWebRTCConnected()) {
      const [{ token }] = await getTokenByActionAndResourceId(
        [StreamWebRTC.ACTION_PTZ, StreamWebRTC.ACTION_PTZ_AREA_ZOOM_IN],
        +streamingCamera.cameraId,
        user,
        ResDeviceType.CAMERA,
        streamingCamera.orgId,
      );

      if (token) {
        streamControllerRef.current?.ptz({ cmd, token });
        handleUpdatePtzSnapshot();
        console.log("---ptz move is started!!!", cmd);
      } else {
        console.error("ptz token is empty");
      }
    } else {
      console.error("the stream webrtc is not connected");
    }
  }
  useEffect(() => {
    const _ptz = async (_, { cmd }) => {
      console.log("ptz start: ", cmd);
      ptz(cmd);
    };
    const subscriptionPtz = PubSub.subscribe("focusPtzControl", _ptz);
    const subscriptionSnapshot = PubSub.subscribe(
      "focusPtzControlSnapshot",
      takeSnapshots,
    );
    return () => {
      PubSub.unsubscribe("focusPtzControl", subscriptionPtz);
      PubSub.unsubscribe("focusPtzControlSnapshot", subscriptionSnapshot);
    };
  }, [streamingCamera]);

  const hanlePtzToPosition = throttle(async (e) => {
    const rect = refCanvasWrapper.current.getBoundingClientRect();
    const id = CANVAS_ID_PREFIX + streamingCamera.cameraId;
    const canvas = document.getElementById(id) as HTMLCanvasElement;
    const width = canvas.width;
    const height = canvas.height;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    console.log("ptz to position", x, y, width, height);

    const [{ token }] = await getTokenByActionAndResourceId(
      [StreamWebRTC.ACTION_PTZ, StreamWebRTC.ACTION_PTZ_AREA_ZOOM_IN],
      +streamingCamera.cameraId,
      user,
      ResDeviceType.CAMERA,
      streamingCamera.orgId,
    );
    if (!token) {
      console.error("ptz token is empty");
      return;
    }
    const param: PtzAreaZoomParams = {
      token,
      camera: {
        macAddress: streamingCamera.cameraMac,
        id: streamingCamera.cameraId,
        remoteIndex: streamingCamera.remoteIndex,
      },
      area: {
        centerPointX: Math.round((x / rect.width) * width),
        centerPointY: Math.round((y / rect.height) * height),
        lengthX: 0,
        lengthY: 0,
        width: width,
        height: height,
      },
    };
    streamControllerRef.current?.ptzAreaZoomIn(param);
    console.log("---ptz move is started!!!");
  }, 1000);

  const streamingStateMask = () => {
    if (
      (firstFrameDrew && streamState === "open") ||
      streamType === STREAM_TYPE_PLAYBACK
    ) {
      return <></>;
    }
    if (streamState === "inActive") {
      return (
        <div className="w-full h-full absolute top-0 flex justify-center items-center z-[1] flex-col bg-zinc-100 text-neutral-500 fill-neutral-500 gap-3 overflow-y-hidden">
          <div className="absolute top-1/2 left-1/2 flex justify-center items-center flex-col -translate-y-1/2 -translate-x-1/2">
            <div
              className="ml-1 inline-block h-8 w-8 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
            ></div>
            <div className="">Stream paused due to inactivity</div>
          </div>
        </div>
      );
    }
    if (indexDBBlob) {
      return (
        <div className="absolute flex justify-center items-center object-contain max-w-full max-h-full w-full h-full">
          <img
            src={URL.createObjectURL(indexDBBlob)}
            alt="Camera Placeholder"
            className="object-contain max-w-full max-h-full w-full h-full"
          ></img>
        </div>
      );
    }
    if (streamState === "closed" || streamState === "error") {
      return (
        <div className="w-full h-full absolute top-0 flex justify-center items-center z-[1] flex-col bg-zinc-100 text-neutral-500 fill-neutral-500 gap-3 overflow-y-hidden">
          <div className="text-center">
            Please retry or check camera status and connection.
          </div>
          <button
            className="hover:fill-neutral-400"
            onClick={() => {
              playVideo();
            }}
          >
            <SvgRefresh width="28" />
          </button>
        </div>
      );
    }
    return (
      <div className="w-full h-full absolute top-0 flex justify-center items-center z-[1] flex-col bg-zinc-100 text-neutral-500 fill-neutral-500 gap-3 overflow-y-hidden">
        <div className="absolute top-1/2 left-1/2 flex justify-center items-center flex-col -translate-y-1/2 -translate-x-1/2">
          <div
            className="ml-1 inline-block h-8 w-8 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="status"
          ></div>
          <div className="">Loading streaming...</div>
        </div>
      </div>
    );
  };
  return (
    <>
      <div
        className={clsx(
          "w-full max-w-100 max-h-100 relative border1-4 rounded-2xl1 overflow-hidden group",
          visible === false && "hidden",
        )}
        onClick={handleSelectCamera}
        onDoubleClick={handleDoubleClickCamera}
        ref={refContainer}
        style={{
          boxShadow: selected && "0px 0px 0px 4px rgba(37,99,235,1)",
        }}
      >
        <video id="video" ref={refVideo} className="hidden" />
        <TransformWrapper
          initialScale={1}
          initialPositionX={0}
          initialPositionY={0}
          centerOnInit={true}
          disablePadding={true}
          doubleClick={{ disabled: true }}
          ref={refReactZoomPanPinchRef}
        >
          {({ zoomIn, zoomOut }) => (
            <React.Fragment>
              <TransformComponent
                wrapperClass="!w-full !h-full"
                contentClass="!w-full !h-full"
              >
                <div
                  ref={refCanvasWrapper}
                  className={clsx(
                    "aspect-video w-full h-full  flex justify-center",
                    ptzMode === "mouse" ? "cursor-crosshair" : "cursor-default",
                  )}
                  onClick={ptzMode === "mouse" ? hanlePtzToPosition : () => {}}
                  style={{
                    backgroundColor: contentFillColor
                      ? "#1a1a1a"
                      : "rgb(244 244 245 / var(--tw-bg-opacity))",
                  }}
                ></div>
                {streamingStateMask()}
              </TransformComponent>
              <div
                className="tools absolute bottom-4 right-4 flex flex-row items-center gap-x-3"
                onDoubleClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {streamingCamera?.capabilities?.ptz &&
                  showPtz &&
                  ptzMode !== "mouse" && (
                    <div style={{ width: "100px", height: "100px" }}>
                      <PtzController
                        funcPtz={(cmd) => ptz(cmd)}
                        funcSnapshot={() => takeSnapshots()}
                      />
                    </div>
                  )}
                <div className="flex flex-col opacity-0 transition duration-300 ease-in-out group-hover:opacity-100">
                  {streamingCamera?.capabilities?.ptz ? (
                    <>
                      <button
                        onMouseDown={() => ptz("ZoomIn")}
                        onMouseUp={() => ptz("Stop")}
                        onMouseLeave={() => ptz("Stop")}
                      >
                        <div className="w-7 h-7 rounded-t-3xl flex items-center justify-center bg-neutral-800 bg-opacity-75 border-b border-zinc-500 ">
                          <PlusIcon className="w-4 text-white" />
                        </div>
                      </button>
                      <button
                        onMouseDown={() => ptz("ZoomOut")}
                        onMouseUp={() => ptz("Stop")}
                        onMouseLeave={() => ptz("Stop")}
                      >
                        <div className="w-7 h-6 rounded-b-3xl flex items-center justify-center bg-neutral-800 bg-opacity-75 ">
                          <MinusIcon className="w-4 text-white" />
                        </div>
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => zoomIn()}>
                        <div className="w-7 h-7 rounded-t-3xl flex items-center justify-center bg-neutral-800 bg-opacity-75 border-b border-zinc-500 ">
                          <PlusIcon className="w-4 text-white" />
                        </div>
                      </button>
                      <button onClick={() => zoomOut()}>
                        <div className="w-7 h-6 rounded-b-3xl flex items-center justify-center bg-neutral-800 bg-opacity-75 ">
                          <MinusIcon className="w-4 text-white" />
                        </div>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </React.Fragment>
          )}
        </TransformWrapper>
        <audio id="audio" ref={refAudio} autoPlay></audio>
        {children}
        {streamState !== "open" && (
          <div className="w-full h-full bg-transparent">
            <div
              className="absolute right-3 top-3  rounded-lg text-white px-2 py-1"
              style={{
                backgroundColor: connectionText
                  ? "rgba(22,22,22,0.65)"
                  : "transparent",
              }}
            >
              {connectionText}
              <span
                className="w-[8px] aspect-square bg-[#FF9800] rounded-full"
                style={{ display: "inline-block", marginLeft: "5px" }}
              />
            </div>
          </div>
        )}
        {streamState === "open" && !firstFrameDrew && (
          <div className="w-full h-full bg-transparent">
            <div
              className="absolute right-3 top-3  rounded-lg text-white px-2 py-1"
              style={{
                backgroundColor: "transparent",
              }}
            >
              <span
                className="w-[8px] aspect-square rounded-full"
                style={{
                  display: "inline-block",
                  marginLeft: "5px",
                  backgroundColor: "blue",
                }}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
});

export default memo(CameraPlayer);
// export default CameraPlayer;
